/**
 * Browser-environment glue (app layer — DOM access is allowed here; the DOM-free core is not). This
 * centralizes the feature-detected adapters the App and the diagnostics page both need: a resilient
 * localStorage KeyValueStore, a capability snapshot, and storage-integrity probes. All degrade
 * gracefully where an API is missing.
 */
import type { Capabilities, KeyValueStore, StorageProbe } from '../core/index.ts';
import { openSessionCache } from '../core/index.ts';

/** A resilient localStorage adapter (feature-detected; degrades to an in-memory map). */
export function browserKeyValueStore(): KeyValueStore {
  try {
    const ls = globalThis.localStorage;
    ls.getItem('ts.probe');
    return {
      get: (k) => ls.getItem(k),
      set: (k, v) => ls.setItem(k, v),
      remove: (k) => ls.removeItem(k),
    };
  } catch {
    const map = new Map<string, string>();
    return {
      get: (k) => map.get(k) ?? null,
      set: (k, v) => void map.set(k, v),
      remove: (k) => void map.delete(k),
    };
  }
}

function hasLocalStorage(): boolean {
  try {
    const ls = globalThis.localStorage;
    const key = 'ts.probe';
    ls.setItem(key, '1');
    const ok = ls.getItem(key) === '1';
    ls.removeItem(key);
    return ok;
  } catch {
    return false;
  }
}

/** Snapshot the browser capabilities the diagnostics page reports (web-client.md §8). */
export async function gatherCapabilities(): Promise<Capabilities> {
  const nav = safeNavigator();
  let storageQuotaBytes: number | null = null;
  try {
    const estimate = nav?.storage?.estimate?.bind(nav.storage);
    if (estimate) {
      const est = await estimate();
      storageQuotaBytes = est.quota ?? null;
    }
  } catch {
    /* ignore */
  }
  let prefersReducedMotion = false;
  try {
    prefersReducedMotion =
      globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    /* ignore */
  }
  return {
    serviceWorker: !!nav && 'serviceWorker' in nav,
    webSocket: typeof globalThis.WebSocket !== 'undefined',
    indexedDb: typeof globalThis.indexedDB !== 'undefined',
    localStore: hasLocalStorage(),
    prefersReducedMotion,
    storageQuotaBytes,
  };
}

/** Round-trip a probe value through localStorage and IndexedDB to verify integrity. */
export async function storageProbe(): Promise<StorageProbe> {
  return { localStore: hasLocalStorage(), indexedDb: await probeIndexedDb() };
}

async function probeIndexedDb(): Promise<boolean> {
  if (typeof globalThis.indexedDB === 'undefined') return false;
  try {
    const cache = await openSessionCache();
    const key = 'ts.probe';
    await cache.put(key, { at: Date.now() });
    const back = await cache.get<{ at: number }>(key);
    await cache.remove(key);
    return back !== null;
  } catch {
    return false;
  }
}

interface NavigatorLike {
  serviceWorker?: unknown;
  storage?: { estimate?: () => Promise<{ quota?: number }> };
}

function safeNavigator(): NavigatorLike | null {
  try {
    return globalThis.navigator as unknown as NavigatorLike;
  } catch {
    return null;
  }
}
